import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bike, KeyRound, Link2, Pencil, Plus, Trash2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { ABtn, AInput, ALabel, ASelect, Panel } from "./AdminLayout";

const EMPTY_COURIER = { name: "", phone: "", zone_id: "", vehicle: "", is_active: true, email: "", password: "" };

export function AdminCouriers() {
  const [couriers, setCouriers] = useState([]);
  const [zones, setZones] = useState([]);
  const [form, setForm] = useState(null);

  const load = async () => {
    try {
      const [c, z] = await Promise.all([api.get("/admin/couriers"), api.get("/delivery-zones", { params: { all: true } })]);
      setCouriers(c.data);
      setZones(z.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    const payload = {
      name: form.name,
      phone: form.phone,
      zone_id: form.zone_id || null,
      vehicle: form.vehicle,
      is_active: form.is_active,
      email: form.email || null,
      password: form.password || null,
    };
    try {
      if (form.id) await api.put(`/admin/couriers/${form.id}`, payload);
      else await api.post("/admin/couriers", payload);
      toast.success("Livreur enregistré");
      setForm(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer le livreur « ${c.name} » ?`)) return;
    try {
      await api.delete(`/admin/couriers/${c.id}`);
      toast.success("Livreur supprimé");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-couriers-page">
      <Panel
        title="Livreurs"
        subtitle={`${couriers.length} livreur(s) · tournées depuis l'entrepôt`}
        actions={
          <ABtn onClick={() => setForm({ ...EMPTY_COURIER })} data-testid="admin-new-courier-button">
            <Plus className="h-4 w-4" /> Nouveau livreur
          </ABtn>
        }
        testid="admin-couriers-panel"
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-slate-400">
              <tr className="border-b border-slate-800">
                <th className="py-2">Livreur</th>
                <th className="py-2">Téléphone</th>
                <th className="py-2">Zone</th>
                <th className="py-2">Véhicule</th>
                <th className="py-2">Compte</th>
                <th className="py-2 text-right">En cours</th>
                <th className="py-2 text-right">Livrées</th>
                <th className="py-2">Statut</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {couriers.length === 0 && (
                <tr><td colSpan={9} className="py-4 text-sm text-slate-500">Aucun livreur enregistré.</td></tr>
              )}
              {couriers.map((c) => (
                <tr key={c.id} className="border-b border-slate-800/60" data-testid={`admin-courier-row-${c.phone}`}>
                  <td className="py-2 font-medium text-white"><Bike className="mr-1 inline h-3.5 w-3.5 text-gold" />{c.name}</td>
                  <td className="font-num py-2 text-slate-300">{c.phone}</td>
                  <td className="py-2 text-slate-300">{c.zone_name || "Toutes zones"}</td>
                  <td className="py-2 text-slate-400">{c.vehicle || "—"}</td>
                  <td className="py-2 text-[11px] text-slate-400">{c.email || "sans compte"}</td>
                  <td className="font-num py-2 text-right text-amber-300">{c.active_deliveries}</td>
                  <td className="font-num py-2 text-right text-emerald-400">{c.delivered_total}</td>
                  <td className="py-2">
                    <span className={`ds-chip ${c.is_active ? "border-emerald-700 bg-emerald-950 text-emerald-300" : "border-slate-700 bg-slate-800 text-slate-400"}`}>
                      {c.is_active ? "Actif" : "Inactif"}
                    </span>
                  </td>
                  <td className="py-2">
                    <div className="flex justify-end gap-1">
                      <ABtn variant="ghost" onClick={() => setForm({ ...EMPTY_COURIER, ...c, zone_id: c.zone_id || "", password: "" })} data-testid={`admin-edit-courier-${c.phone}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </ABtn>
                      <ABtn variant="danger" onClick={() => remove(c)} data-testid={`admin-delete-courier-${c.phone}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </ABtn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {form && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" data-testid="admin-courier-modal">
          <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-white">{form.id ? "Modifier le livreur" : "Nouveau livreur"}</h3>
              <ABtn variant="ghost" onClick={() => setForm(null)}><X className="h-4 w-4" /></ABtn>
            </div>
            <div className="mt-4 space-y-3">
              <div>
                <ALabel>Nom complet *</ALabel>
                <AInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="courier-form-name" />
              </div>
              <div>
                <ALabel>Téléphone *</ALabel>
                <AInput value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="courier-form-phone" />
              </div>
              <div>
                <ALabel>Zone principale</ALabel>
                <ASelect value={form.zone_id} onChange={(e) => setForm({ ...form, zone_id: e.target.value })} data-testid="courier-form-zone">
                  <option value="">Toutes zones</option>
                  {zones.map((z) => (
                    <option key={z.id} value={z.id}>{z.name}</option>
                  ))}
                </ASelect>
              </div>
              <div>
                <ALabel>Véhicule</ALabel>
                <AInput value={form.vehicle} onChange={(e) => setForm({ ...form, vehicle: e.target.value })} placeholder="Moto, tricycle, voiture…" data-testid="courier-form-vehicle" />
              </div>
              <div className="rounded-xl border border-slate-800 bg-slate-950 p-3">
                <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  <KeyRound className="h-3.5 w-3.5" /> Compte de connexion (optionnel)
                </p>
                <div className="mt-2 space-y-2">
                  <AInput
                    value={form.email || ""}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="email@digitalshop.gn"
                    disabled={Boolean(form.id && form.email)}
                    data-testid="courier-form-email"
                  />
                  <AInput
                    type="password"
                    value={form.password || ""}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder={form.id ? "Nouveau mot de passe (facultatif)" : "Mot de passe (6 caractères min.)"}
                    data-testid="courier-form-password"
                  />
                </div>
                <p className="mt-2 text-[11px] text-slate-500">
                  Avec un compte, le livreur suit ses tournées depuis /livreur. Sans compte, seul l'admin met à jour ses livraisons.
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="courier-form-active" />
                Livreur actif
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <ABtn variant="ghost" onClick={() => setForm(null)}>Annuler</ABtn>
              <ABtn onClick={save} data-testid="courier-form-save">Enregistrer</ABtn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminSettings() {
  const [links, setLinks] = useState(null);
  const [payments, setPayments] = useState(null);
  const [payInfo, setPayInfo] = useState(null);
  const [banner, setBanner] = useState(null);
  const [draft, setDraft] = useState({
    orange_money: { enabled: false, merchant_id: "", api_key: "", api_secret: "", callback_url: "", environment: "test" },
    mtn_momo: { enabled: false, merchant_id: "", api_key: "", api_secret: "", callback_url: "", environment: "test" },
  });

  const load = async () => {
    try {
      const [l, p] = await Promise.all([api.get("/app-links"), api.get("/admin/payment-settings")]);
      setLinks(l.data);
      setPayments(p.data);
      setPayInfo((await api.get("/payment-info")).data);
      setBanner((await api.get("/banner")).data);
      setDraft({
        orange_money: { ...p.data.orange_money, api_key: "", api_secret: "" },
        mtn_momo: { ...p.data.mtn_momo, api_key: "", api_secret: "" },
      });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveLinks = async () => {
    try {
      await api.put("/admin/app-links", links);
      toast.success("Liens et QR codes mis à jour");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const savePayments = async () => {
    try {
      await api.put("/admin/payment-settings", draft);
      toast.success("Paramètres de paiement enregistrés");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const savePayInfo = async () => {
    try {
      await api.put("/admin/payment-info", payInfo);
      toast.success("Coordonnées de paiement enregistrées");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveBanner = async () => {
    try {
      await api.put("/admin/banner", banner);
      toast.success("Bandeau promotionnel enregistré");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!links || !payments) return <p className="text-sm text-slate-400">Chargement…</p>;

  const providers = [
    { id: "orange_money", label: "Orange Money Guinée" },
    { id: "mtn_momo", label: "MTN Mobile Money" },
  ];

  return (
    <div className="space-y-4" data-testid="admin-settings-page">
      {banner && (
        <Panel
          title="Bandeau promotionnel"
          subtitle="Message affiché en haut de la boutique (cliquable, masquable par le visiteur)"
          actions={<ABtn onClick={saveBanner} data-testid="banner-save">Enregistrer</ABtn>}
          testid="admin-banner-panel"
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <ALabel>Texte du bandeau</ALabel>
              <AInput
                value={banner.text || ""}
                onChange={(e) => setBanner({ ...banner, text: e.target.value })}
                placeholder="Livraison offerte dès 100 000 GNF d'achat"
                data-testid="banner-text"
              />
            </div>
            <div>
              <ALabel>Lien au clic</ALabel>
              <AInput
                value={banner.link || ""}
                onChange={(e) => setBanner({ ...banner, link: e.target.value })}
                placeholder="/catalogue"
                data-testid="banner-link"
              />
            </div>
            <label className="flex items-end gap-2 pb-2 text-sm text-slate-300">
              <input
                type="checkbox"
                checked={!!banner.enabled}
                onChange={(e) => setBanner({ ...banner, enabled: e.target.checked })}
                data-testid="banner-enabled"
              />
              Afficher le bandeau
            </label>
          </div>
        </Panel>
      )}
      {payInfo && (
        <Panel
          title="Coordonnées de paiement affichées au client"
          subtitle="Numéros marchands et consignes visibles sur la page de commande (paiement à la commande)"
          actions={<ABtn onClick={savePayInfo} data-testid="payinfo-save">Enregistrer</ABtn>}
          testid="admin-payinfo-panel"
        >
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ["orange_number", "Numéro Orange Money marchand"],
              ["orange_name", "Nom du compte Orange Money"],
              ["mtn_number", "Numéro MTN MoMo marchand"],
              ["mtn_name", "Nom du compte MTN MoMo"],
              ["bank_name", "Banque (virement)"],
              ["bank_account", "Numéro de compte bancaire"],
            ].map(([key, label]) => (
              <div key={key}>
                <ALabel>{label}</ALabel>
                <AInput
                  value={payInfo[key] || ""}
                  onChange={(e) => setPayInfo({ ...payInfo, [key]: e.target.value })}
                  data-testid={`payinfo-${key}`}
                />
              </div>
            ))}
            <div className="sm:col-span-2">
              <ALabel>Consignes de paiement affichées au client</ALabel>
              <textarea
                rows={3}
                value={payInfo.instructions || ""}
                onChange={(e) => setPayInfo({ ...payInfo, instructions: e.target.value })}
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-brand"
                data-testid="payinfo-instructions"
              />
            </div>
          </div>
        </Panel>
      )}
      <Panel
        title="Liens & QR codes de l'accueil"
        subtitle="Adresse du site, WhatsApp de commande et lien de l'application mobile"
        actions={<ABtn onClick={saveLinks} data-testid="links-save"><Link2 className="h-4 w-4" /> Enregistrer</ABtn>}
        testid="admin-links-panel"
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <ALabel>Adresse du site (QR « Ouvrir la boutique »)</ALabel>
            <AInput value={links.site_url} onChange={(e) => setLinks({ ...links, site_url: e.target.value })} placeholder={window.location.origin} data-testid="links-site-url" />
          </div>
          <div>
            <ALabel>Numéro WhatsApp de commande</ALabel>
            <AInput value={links.whatsapp_number} onChange={(e) => setLinks({ ...links, whatsapp_number: e.target.value })} placeholder="+224 6XX XX XX XX" data-testid="links-whatsapp-number" />
          </div>
          <div className="sm:col-span-2">
            <ALabel>Message WhatsApp pré-rempli</ALabel>
            <AInput value={links.whatsapp_message} onChange={(e) => setLinks({ ...links, whatsapp_message: e.target.value })} data-testid="links-whatsapp-message" />
          </div>
          <div>
            <ALabel>Lien Play Store / App Store</ALabel>
            <AInput value={links.store_url} onChange={(e) => setLinks({ ...links, store_url: e.target.value })} placeholder="https://play.google.com/store/apps/…" data-testid="links-store-url" />
          </div>
          <div>
            <ALabel>Libellé du QR application</ALabel>
            <AInput value={links.store_label} onChange={(e) => setLinks({ ...links, store_label: e.target.value })} data-testid="links-store-label" />
          </div>
        </div>
      </Panel>

      <Panel
        title="Paiements mobiles (Orange Money / MTN)"
        subtitle="Clés marchandes à brancher dès réception de vos accès officiels"
        actions={<ABtn onClick={savePayments} data-testid="payments-save">Enregistrer</ABtn>}
        testid="admin-payments-panel"
      >
        <p className="rounded-xl border border-amber-900/60 bg-amber-950/30 p-3 text-xs text-amber-200" data-testid="payments-note">
          {payments.note}
        </p>
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          {providers.map((p) => (
            <div key={p.id} className="rounded-xl border border-slate-800 bg-slate-950 p-4" data-testid={`payment-provider-${p.id}`}>
              <div className="flex items-center justify-between">
                <p className="font-display text-sm font-bold text-white">{p.label}</p>
                <span className={`ds-chip ${payments[p.id].configured ? "border-emerald-700 bg-emerald-950 text-emerald-300" : "border-slate-700 bg-slate-800 text-slate-400"}`}>
                  {payments[p.id].configured ? "Clés enregistrées" : "Non configuré"}
                </span>
              </div>
              <div className="mt-3 space-y-2">
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    checked={draft[p.id].enabled}
                    onChange={(e) => setDraft({ ...draft, [p.id]: { ...draft[p.id], enabled: e.target.checked } })}
                    data-testid={`payment-${p.id}-enabled`}
                  />
                  Activer ce moyen de paiement
                </label>
                <div>
                  <ALabel>Identifiant marchand</ALabel>
                  <AInput value={draft[p.id].merchant_id} onChange={(e) => setDraft({ ...draft, [p.id]: { ...draft[p.id], merchant_id: e.target.value } })} data-testid={`payment-${p.id}-merchant`} />
                </div>
                <div>
                  <ALabel>Clé API {payments[p.id].api_key_masked && `(actuelle : ${payments[p.id].api_key_masked})`}</ALabel>
                  <AInput type="password" value={draft[p.id].api_key} onChange={(e) => setDraft({ ...draft, [p.id]: { ...draft[p.id], api_key: e.target.value } })} placeholder="laisser vide pour conserver" data-testid={`payment-${p.id}-key`} />
                </div>
                <div>
                  <ALabel>Secret API {payments[p.id].api_secret_masked && `(actuel : ${payments[p.id].api_secret_masked})`}</ALabel>
                  <AInput type="password" value={draft[p.id].api_secret} onChange={(e) => setDraft({ ...draft, [p.id]: { ...draft[p.id], api_secret: e.target.value } })} placeholder="laisser vide pour conserver" data-testid={`payment-${p.id}-secret`} />
                </div>
                <div>
                  <ALabel>URL de notification (callback)</ALabel>
                  <AInput value={draft[p.id].callback_url} onChange={(e) => setDraft({ ...draft, [p.id]: { ...draft[p.id], callback_url: e.target.value } })} data-testid={`payment-${p.id}-callback`} />
                </div>
                <div>
                  <ALabel>Environnement</ALabel>
                  <ASelect value={draft[p.id].environment} onChange={(e) => setDraft({ ...draft, [p.id]: { ...draft[p.id], environment: e.target.value } })} data-testid={`payment-${p.id}-env`}>
                    <option value="test">Test / sandbox</option>
                    <option value="live">Production</option>
                  </ASelect>
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-slate-500">
          Les secrets sont stockés côté serveur et jamais renvoyés en clair. Tant qu'aucune API n'est branchée,
          une commande Mobile Money reste « Paiement en attente » jusqu'à validation manuelle.
          Dernière mise à jour : {fmtDate(payments.updated_at)}
        </p>
      </Panel>
    </div>
  );
}
